import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { X, Minus, Plus, Scissors, Loader } from 'lucide-react'
import useCreateClip from '../../hooks/useCreateClip'
import { makeThrottle } from '../../utils/throttle'

// Overlay de decoupe pose EN ABSOLU sur le bas du player. La video reste
// visible derriere un degrade noir. Remplace InlineTrimBar (qui poussait la
// video hors du viewport). Le player masque ses controles natifs pendant ce
// mode (prop hideControls) pour eviter le doublon.
//
// NB : on recoit playerRef (imperative handle) et videoUrl. playerRef sert au
// scrub de la video principale pendant le drag ; videoUrl alimente un <video>
// offscreen pour les thumbnails de la timeline.

const BLUE = '#4F8EF7'
const RED  = '#E24B4A'

const DISP = "'Barlow Condensed', sans-serif"
const BODY = "'Space Grotesk', sans-serif"
const MONO = "'JetBrains Mono', 'Space Grotesk', monospace"

const MIN_DUR = 5
const MAX_DUR = 60
const WINDOW_PAD = 45
const THUMB_COUNT = 16
const KEY_STEP = 0.5

const TYPES = [
  { value: 'goal',               label: 'But',        short: 'But',       color: '#4F8EF7' },
  { value: 'chance',             label: 'Occasion',   short: 'Occas',     color: '#f59e0b' },
  { value: 'loss',               label: 'Perte',      short: 'Perte',     color: '#dc2626' },
  { value: 'recovery',           label: 'Récup',      short: 'Récup',     color: '#3DBF7A' },
  { value: 'transition_for',     label: 'Trans off.', short: 'Trans off', color: '#3b82f6' },
  { value: 'transition_against', label: 'Trans sub.', short: 'Trans sub', color: '#991b1b' },
  { value: 'defensive_phase',    label: 'Défense',    short: 'Déf',       color: '#6b7280' },
  { value: 'set_piece',          label: 'CPA',        short: 'CPA',       color: '#a855f7' },
]

// MM:SS.mmm (desktop) / MM:SS.d (mobile, 1 decimale)
function fmtTC(s, short) {
  if (!Number.isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  const frac = s - Math.floor(s)
  const mm = String(m).padStart(2, '0')
  const ss = String(sec).padStart(2, '0')
  if (short) return `${mm}:${ss}.${Math.floor(frac * 10)}`
  return `${mm}:${ss}.${String(Math.floor(frac * 1000)).padStart(3, '0')}`
}

export default function TrimOverlay({
  playerRef,
  videoUrl,
  videoDuration = 0,
  currentTime = 0,
  matchId,
  half,
  onCancel,
  onSuccess,
  showToast,
}) {
  const { generate, isGenerating, pollStatus, error } = useCreateClip()

  const [effDuration, setEffDuration] = useState(videoDuration || 0)

  const windowRef = useRef(null)
  if (windowRef.current === null && (effDuration > 0 || videoDuration > 0)) {
    const dur = effDuration || videoDuration
    let ws = Math.max(0, currentTime - WINDOW_PAD)
    let we = Math.min(dur, currentTime + WINDOW_PAD)
    if (we - ws < Math.min(dur, 2 * WINDOW_PAD)) {
      if (ws === 0) we = Math.min(dur, 2 * WINDOW_PAD)
      else if (we === dur) ws = Math.max(0, dur - 2 * WINDOW_PAD)
    }
    windowRef.current = { start: ws, end: we }
  }
  const win = windowRef.current || { start: Math.max(0, currentTime - WINDOW_PAD), end: currentTime + WINDOW_PAD }

  const [inTime, setInTime] = useState(() => Math.max(0, currentTime - 5))
  const [outTime, setOutTime] = useState(() => {
    const d = videoDuration || currentTime + 10
    return Math.min(d, currentTime + 10)
  })
  const [tag, setTag] = useState(null)
  const [note, setNote] = useState('')
  const [noteExpanded, setNoteExpanded] = useState(false)
  const [thumbs, setThumbs] = useState([])
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  const overlayRef = useRef(null)
  const trackRef = useRef(null)
  const dragRef = useRef(null)
  const rangeGrabRef = useRef(0)
  const seekThrottle = useRef(makeThrottle(40)).current

  // Refs pour peindre le drag en imperatif (0 re-render React pendant le
  // pointermove -> le thread reste libre, le seekTo suit en continu). Les
  // valeurs live sont commitees au state seulement au pointerup.
  const inHandleRef = useRef(null)
  const outHandleRef = useRef(null)
  const selBoxRef = useRef(null)
  const leftMaskRef = useRef(null)
  const rightMaskRef = useRef(null)
  const inTcRef = useRef(null)
  const outTcRef = useRef(null)
  const durRef = useRef(null)
  const inLiveRef = useRef(inTime)
  const outLiveRef = useRef(outTime)

  // Peint les positions (poignees, selection, masques, timecodes, duree) en
  // imperatif a partir de ni/no, sans passer par le state React.
  const paintDrag = (ni, no) => {
    const s = Math.max(0.001, win.end - win.start)
    const inP = ((ni - win.start) / s) * 100
    const outP = ((no - win.start) / s) * 100
    if (leftMaskRef.current) leftMaskRef.current.style.width = `${inP}%`
    if (rightMaskRef.current) rightMaskRef.current.style.left = `${outP}%`
    if (selBoxRef.current) { selBoxRef.current.style.left = `${inP}%`; selBoxRef.current.style.width = `${Math.max(0, outP - inP)}%` }
    if (inHandleRef.current) inHandleRef.current.style.left = `${inP}%`
    if (outHandleRef.current) outHandleRef.current.style.left = `${outP}%`
    if (inTcRef.current) inTcRef.current.textContent = `In ${fmtTC(ni, isMobile)}`
    if (outTcRef.current) outTcRef.current.textContent = `Out ${fmtTC(no, isMobile)}`
    if (durRef.current) durRef.current.textContent = `${(no - ni).toFixed(1)}s`
  }

  // Si un re-render survient PENDANT le drag (ex : le scrub met a jour le
  // currentTime du parent ~4/s), React reappliquerait les positions du state
  // committe et ferait sauter les poignees. On re-peint depuis les live refs.
  useLayoutEffect(() => {
    if (dragRef.current) paintDrag(inLiveRef.current, outLiveRef.current)
  })

  const duration = outTime - inTime
  const durationInvalid = duration < MIN_DUR - 0.001 || duration > MAX_DUR + 0.001 || inTime < 0
  const canGenerate = !durationInvalid && !isGenerating && !!videoUrl && !!matchId

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!effDuration) {
      const d = playerRef?.current?.getDuration?.() || 0
      if (d > 0) setEffDuration(d)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Focus trap + Escape (role=dialog)
  useEffect(() => {
    const el = overlayRef.current
    if (!el) return
    const focusables = () => Array.from(
      el.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(n => !n.disabled && n.offsetParent !== null)
    focusables()[0]?.focus?.()
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel && onCancel(); return }
      if (e.key !== 'Tab') return
      const f = focusables()
      if (f.length === 0) return
      const first = f[0], last = f[f.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    el.addEventListener('keydown', onKey)
    return () => el.removeEventListener('keydown', onKey)
  }, [onCancel])

  const clampIn = useCallback((v, out) => {
    v = Math.max(win.start, Math.max(0, v))
    v = Math.min(v, out - MIN_DUR)
    v = Math.max(v, out - MAX_DUR)
    return v
  }, [win.start])

  const clampOut = useCallback((v, inn) => {
    const hi = effDuration || videoDuration || win.end
    v = Math.min(win.end, Math.min(hi, v))
    v = Math.max(v, inn + MIN_DUR)
    v = Math.min(v, inn + MAX_DUR)
    return v
  }, [win.end, effDuration, videoDuration])

  // Scrub live de la video principale : seekTo en continu (throttle 40ms)
  // pendant le drag ; force=true pour caler la frame finale (pointerup /
  // fine-tune). Le player est mis en pause au debut du drag (onHandleDown),
  // pas a chaque frame. Au release on reste sur la derniere frame (pas de reset).
  const scrub = (t, force = false) => {
    const p = playerRef?.current
    if (!p) return
    const doIt = () => { try { p.seekTo(t) } catch { /* noop */ } }
    if (force) doIt()
    else seekThrottle(doIt)
  }

  // ─── Thumbnails (offscreen, best-effort) ──────────────────────────────────
  useEffect(() => {
    if (!videoUrl) return
    let cancelled = false
    const v = document.createElement('video')
    v.crossOrigin = 'anonymous'
    v.preload = 'auto'
    v.muted = true
    v.playsInline = true
    v.src = videoUrl

    const canvas = document.createElement('canvas')
    canvas.width = 96
    canvas.height = 54
    const ctx = canvas.getContext('2d')

    const once = (el, ev, timeout = 8000) => new Promise((resolve, reject) => {
      const to = setTimeout(() => { cleanup(); reject(new Error('timeout')) }, timeout)
      const ok = () => { cleanup(); resolve() }
      const cleanup = () => { clearTimeout(to); el.removeEventListener(ev, ok) }
      el.addEventListener(ev, ok, { once: true })
    })

    const run = async () => {
      try {
        await once(v, 'loadedmetadata')
        let w = windowRef.current
        if (!w) {
          const dur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : currentTime + WINDOW_PAD
          w = { start: Math.max(0, currentTime - WINDOW_PAD), end: Math.min(dur, currentTime + WINDOW_PAD) }
        }
        const { start, end } = w
        const out = new Array(THUMB_COUNT).fill(null)
        for (let i = 0; i < THUMB_COUNT; i++) {
          if (cancelled) return
          const t = start + (i / (THUMB_COUNT - 1)) * (end - start)
          v.currentTime = Math.max(0, t)
          await once(v, 'seeked')
          if (cancelled) return
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height)
          out[i] = canvas.toDataURL('image/jpeg', 0.5)
          if (!cancelled) setThumbs([...out])
        }
      } catch {
        if (!cancelled) setThumbs([])
      } finally {
        v.removeAttribute('src')
        v.load()
      }
    }
    run()
    return () => { cancelled = true; v.removeAttribute('src'); try { v.load() } catch { /* noop */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl])

  // ─── Drag (pointer capture) ───────────────────────────────────────────────
  const timeFromClientX = useCallback((clientX) => {
    const track = trackRef.current
    if (!track) return inTime
    const rect = track.getBoundingClientRect()
    const ratio = rect.width ? (clientX - rect.left) / rect.width : 0
    return win.start + Math.max(0, Math.min(1, ratio)) * (win.end - win.start)
  }, [win.start, win.end, inTime])

  // Pendant le drag on ne touche PAS au state : on calcule ni/no depuis les
  // live refs, on peint en imperatif et on seek (throttle). Le state est
  // committe au pointerup.
  const applyDragAt = (clientX) => {
    if (clientX == null || !dragRef.current) return
    const t = timeFromClientX(clientX)
    let ni = inLiveRef.current
    let no = outLiveRef.current
    if (dragRef.current === 'in') {
      ni = clampIn(t, outLiveRef.current)
    } else if (dragRef.current === 'out') {
      no = clampOut(t, inLiveRef.current)
    } else if (dragRef.current === 'range') {
      const dur = outLiveRef.current - inLiveRef.current
      const hi = Math.min(win.end, effDuration || videoDuration || win.end)
      ni = t - rangeGrabRef.current
      ni = Math.max(win.start, Math.max(0, ni))
      ni = Math.min(ni, hi - dur)
      no = ni + dur
    }
    inLiveRef.current = ni
    outLiveRef.current = no
    paintDrag(ni, no)
    // seekTo (throttle) vers la poignee active pour que la video suive
    scrub(dragRef.current === 'out' ? no : ni)
  }

  const onHandleDown = (which) => (e) => {
    e.preventDefault(); e.stopPropagation()
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* noop */ }
    playerRef?.current?.pause()   // pause une fois au debut du drag, stay paused
    inLiveRef.current = inTime
    outLiveRef.current = outTime
    if (which === 'range') rangeGrabRef.current = timeFromClientX(e.clientX) - inTime
    dragRef.current = which
  }
  const onHandleMove = (e) => { if (dragRef.current) applyDragAt(e.clientX) }
  const onHandleUp = (e) => {
    const which = dragRef.current
    dragRef.current = null
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
    if (!which) return
    // Commit du state (une seule re-render) : React reprend le positionnement.
    setInTime(inLiveRef.current)
    setOutTime(outLiveRef.current)
    // Frame finale exacte (non throttlee).
    scrub(which === 'out' ? outLiveRef.current : inLiveRef.current, true)
  }

  // ─── Fine-tune (applique a IN) + clavier ──────────────────────────────────
  const nudgeIn = (delta) => { const ni = clampIn(inTime + delta, outTime); setInTime(ni); scrub(ni, true) }
  const nudgeOut = (delta) => { const no = clampOut(outTime + delta, inTime); setOutTime(no); scrub(no, true) }
  const onKeyIn = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeIn(-KEY_STEP) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nudgeIn(KEY_STEP) }
  }
  const onKeyOut = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeOut(-KEY_STEP) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nudgeOut(KEY_STEP) }
  }

  const inMinusDisabled = inTime <= win.start + 0.001 || duration >= MAX_DUR - 0.001
  const inPlusDisabled = duration <= MIN_DUR + 0.001

  const handleGenerate = async () => {
    if (!canGenerate) return
    const res = await generate({ matchId, inTime, outTime, tag, note, half })
    if (res.status === 'ready') onSuccess && onSuccess(res.moment)
    else if (res.status === 'failed') showToast && showToast(res.error || 'Echec de la generation', 'error')
  }

  const span = Math.max(0.001, win.end - win.start)
  const inPct = ((inTime - win.start) / span) * 100
  const outPct = ((outTime - win.start) / span) * 100
  const curPct = ((currentTime - win.start) / span) * 100

  const HANDLE_HIT = 44
  const HANDLE_W = isMobile ? 7 : 8
  const durSize = isMobile ? 18 : 26
  const tcSize = isMobile ? 8 : 9

  // Bouton fine-tune : cible tactile 44x44 (transparent) avec un carre visuel
  // 26px a l'interieur, pour respecter le hit area >= 44px.
  const fineTuneHit = (disabled) => ({
    width: 44, height: 44, minWidth: 44, padding: 0, background: 'none', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0, touchAction: 'manipulation',
  })
  const fineVisual = (disabled) => ({
    width: 26, height: 26, borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: disabled ? 'rgba(255,255,255,0.05)' : 'rgba(79,142,247,0.2)',
    border: `1px solid ${disabled ? 'rgba(255,255,255,0.12)' : 'rgba(79,142,247,0.5)'}`,
    color: disabled ? 'rgba(255,255,255,0.3)' : BLUE,
  })

  const handle = (side) => (
    <div style={{ width: HANDLE_W, height: '100%', background: BLUE, borderRadius: side === 'l' ? '3px 0 0 3px' : '0 3px 3px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.9)' }} />
    </div>
  )

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-label="Mode découpage"
      style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 10,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 30%, rgba(0,0,0,0.95) 100%)',
        padding: isMobile ? '20px 8px 8px' : '40px 14px 14px',
        maxHeight: isMobile ? '50%' : '40%',
        overflowY: 'auto',
        color: '#fff',
      }}
    >
      <style>{`
        .tro-noscroll::-webkit-scrollbar{display:none}
        .tro-handle:focus-visible{outline:2px solid #fff;outline-offset:2px}
        .tro-focus:focus-visible{outline:2px solid ${BLUE};outline-offset:2px}
        @keyframes tro-spin{to{transform:rotate(360deg)}}
        @keyframes tro-slide{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* a) Topbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: BLUE, fontWeight: 700 }}>
            Mode découpage
          </span>
          <span style={{ fontFamily: BODY, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>· ajuste la sélection</span>
        </div>
        <button
          onClick={onCancel}
          aria-label="Annuler le découpage"
          className="tro-focus"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, padding: 4, borderRadius: 6, display: 'flex' }}
        >
          <X size={18} />
        </button>
      </div>

      {/* b) Row principale : duree · timeline · fine-tune */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        {/* Duree */}
        <div style={{ flexShrink: 0, textAlign: 'center' }}>
          <div ref={durRef} style={{ fontFamily: DISP, fontWeight: 700, fontSize: durSize, lineHeight: 1, color: durationInvalid ? RED : BLUE }}>
            {duration.toFixed(1)}s
          </div>
          <div style={{ fontFamily: BODY, fontSize: 9, color: durationInvalid ? RED : 'rgba(255,255,255,0.5)', marginTop: 2, whiteSpace: 'nowrap' }}>
            min {MIN_DUR}s · max {MAX_DUR}s
          </div>
        </div>

        {/* Timeline */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            ref={trackRef}
            style={{
              position: 'relative', height: 32, borderRadius: 6, overflow: 'hidden',
              background: '#151515', userSelect: 'none', touchAction: 'none',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
              {thumbs.length > 0
                ? thumbs.map((src, i) => (
                    <div key={i} style={{ flex: 1, backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  ))
                : <div style={{ flex: 1, background: 'linear-gradient(90deg,#1c1c1c,#232323,#1c1c1c)' }} />
              }
            </div>

            <div ref={leftMaskRef} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${inPct}%`, background: 'rgba(0,0,0,0.7)' }} />
            <div ref={rightMaskRef} style={{ position: 'absolute', top: 0, bottom: 0, left: `${outPct}%`, right: 0, background: 'rgba(0,0,0,0.7)' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${curPct}%`, width: 2, background: 'rgba(255,255,255,0.6)', transform: 'translateX(-1px)' }} />

            <div ref={selBoxRef} style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${inPct}%`, width: `${Math.max(0, outPct - inPct)}%`,
              border: `2px solid ${BLUE}`, borderRadius: 3, boxSizing: 'border-box',
              cursor: 'grab', touchAction: 'none',
            }}
              onPointerDown={onHandleDown('range')}
              onPointerMove={onHandleMove}
              onPointerUp={onHandleUp}
              onPointerCancel={onHandleUp}
            />

            {/* Poignee IN */}
            <div
              ref={inHandleRef}
              role="slider" aria-label="Début du clip" tabIndex={0}
              className="tro-handle"
              aria-valuemin={Math.round(win.start)} aria-valuemax={Math.round(outTime)} aria-valuenow={Math.round(inTime)}
              onPointerDown={onHandleDown('in')} onPointerMove={onHandleMove} onPointerUp={onHandleUp} onPointerCancel={onHandleUp}
              onKeyDown={onKeyIn}
              style={{
                position: 'absolute', top: 0, bottom: 0, left: `${inPct}%`,
                width: HANDLE_HIT, marginLeft: -HANDLE_HIT / 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'ew-resize', touchAction: 'none', zIndex: 3,
              }}
            >
              {handle('l')}
            </div>

            {/* Poignee OUT */}
            <div
              ref={outHandleRef}
              role="slider" aria-label="Fin du clip" tabIndex={0}
              className="tro-handle"
              aria-valuemin={Math.round(inTime)} aria-valuemax={Math.round(win.end)} aria-valuenow={Math.round(outTime)}
              onPointerDown={onHandleDown('out')} onPointerMove={onHandleMove} onPointerUp={onHandleUp} onPointerCancel={onHandleUp}
              onKeyDown={onKeyOut}
              style={{
                position: 'absolute', top: 0, bottom: 0, left: `${outPct}%`,
                width: HANDLE_HIT, marginLeft: -HANDLE_HIT / 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'ew-resize', touchAction: 'none', zIndex: 3,
              }}
            >
              {handle('r')}
            </div>
          </div>

          {/* Timecodes sous la track */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontFamily: MONO, fontSize: tcSize, color: BLUE }}>
            <span ref={inTcRef}>In {fmtTC(inTime, isMobile)}</span>
            <span ref={outTcRef}>Out {fmtTC(outTime, isMobile)}</span>
          </div>
        </div>

        {/* Fine-tune (applique a IN) */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <button aria-label="Début moins 1 seconde" onClick={() => nudgeIn(-1)} disabled={inMinusDisabled} className="tro-focus" style={fineTuneHit(inMinusDisabled)}>
            <span style={fineVisual(inMinusDisabled)}><Minus size={14} /></span>
          </button>
          <button aria-label="Début plus 1 seconde" onClick={() => nudgeIn(1)} disabled={inPlusDisabled} className="tro-focus" style={fineTuneHit(inPlusDisabled)}>
            <span style={fineVisual(inPlusDisabled)}><Plus size={14} /></span>
          </button>
        </div>
      </div>

      {/* d) Textarea note (slide-in au-dessus de la row bas) */}
      {noteExpanded && (
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Ajouter une note optionnelle..."
          rows={2}
          autoFocus
          className="tro-focus"
          style={{
            width: '100%', padding: '8px 10px', borderRadius: 8, marginBottom: 8,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontFamily: BODY, fontSize: 13, outline: 'none',
            resize: 'none', boxSizing: 'border-box', animation: 'tro-slide .18s ease',
          }}
        />
      )}

      {/* c) Row bas : tags scrollables · note · Generer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          className="tro-noscroll"
          style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}
        >
          {TYPES.map(t => {
            const active = tag === t.value
            return (
              <button
                key={t.value}
                onClick={() => setTag(active ? null : t.value)}
                aria-pressed={active}
                className="tro-focus"
                style={{
                  flexShrink: 0, minHeight: 32, padding: '6px 11px', borderRadius: 999, cursor: 'pointer',
                  fontFamily: BODY, fontSize: 12, fontWeight: active ? 600 : 400,
                  background: active ? BLUE : 'rgba(255,255,255,0.08)',
                  color: active ? '#fff' : 'rgba(255,255,255,0.85)',
                  border: `1px solid ${active ? BLUE : 'rgba(255,255,255,0.12)'}`,
                  display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', transition: 'all .12s',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                {isMobile ? t.short : t.label}
              </button>
            )
          })}
        </div>

        {/* Bouton note : pill texte visible (l'ancienne icone seule etait illisible) */}
        <button
          onClick={() => setNoteExpanded(v => !v)}
          aria-label={noteExpanded ? 'Fermer la note' : 'Ajouter une note'}
          aria-pressed={noteExpanded}
          className="tro-focus"
          style={{
            flexShrink: 0, borderRadius: 5, cursor: 'pointer',
            padding: isMobile ? '4px 7px' : '6px 10px',
            fontFamily: BODY, fontSize: isMobile ? 9 : 10, fontWeight: 600,
            background: noteExpanded ? BLUE : 'rgba(255,255,255,0.08)',
            border: `0.5px solid ${noteExpanded ? BLUE : 'rgba(255,255,255,0.15)'}`,
            color: noteExpanded ? '#fff' : 'rgba(255,255,255,0.85)',
            display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            transition: 'all .12s',
          }}
        >
          {noteExpanded ? <X size={12} /> : <Plus size={12} />}
          {noteExpanded ? 'Fermer' : 'Note'}
        </button>

        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="tro-focus"
          style={{
            flexShrink: 0, padding: isMobile ? '7px 12px' : '8px 14px', borderRadius: 8,
            cursor: canGenerate ? 'pointer' : 'not-allowed',
            background: canGenerate ? BLUE : 'rgba(79,142,247,0.4)',
            border: 'none', color: '#fff',
            fontFamily: BODY, fontSize: 13, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {isGenerating
            ? <><Loader size={15} style={{ animation: 'tro-spin 1s linear infinite' }} /> {isMobile ? '' : 'Génération...'}</>
            : <><Scissors size={15} /> Générer</>}
        </button>
      </div>

      {error && pollStatus === 'failed' && (
        <div style={{
          marginTop: 8, fontFamily: BODY, fontSize: 12, color: RED,
        }}>
          {error}
        </div>
      )}
    </div>
  )
}
